import type SecurityService from "./securityService";
import { bound } from "@frui.ts/helpers";
import * as Sentry from "@sentry/react";
import type NotificationService from "./notificationService";
import { NotificationType } from "./notificationService";
import type EventBus from "./eventBus";
import { approved } from "./eventBus";
import type ConfirmationService from "./confirmationService";
import type EnumService from "./enumService";
import type OptionsService from "./optionsService";
import { ApprovalState } from "entities/approvalState";
import type { IApprovable } from "entities/types";
import ApprovalRole from "entities/approvalRole";
import type { IApprovalRepository } from "repositories/types";

export default abstract class ApprovalService<TApprovalRole, TApprovable extends IApprovable<TApprovalRole>> {
  abstract isApprovalProcessComplete(item: TApprovable): boolean;
  abstract currentApproverPredecessor(): TApprovalRole | undefined;
  protected abstract isCurrentStepReadyOrDoneForFactualAuditor(approvals: TApprovalRole[]): boolean;
  protected abstract isCurrentStepReadyOrDoneForCommandOperator(approvals: TApprovalRole[]): boolean;
  protected abstract isCurrentStepReadyOrDoneForChiefAccountant(approvals: TApprovalRole[]): boolean;

  constructor(
    public securityService: SecurityService,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected optionsService: OptionsService
  ) {}

  @bound
  canApprove(item: TApprovable) {
    const identity = this.whoAmI();

    return identity && !this.approvedByApprover(item, identity);
  }

  async approve(
    repository: IApprovalRepository,
    item: TApprovable,
    localization: { title: string; confirm: string; approved: string }
  ) {
    const confirmed = await this.confirmationService.showConfirmation(localization.title, {
      confirmButton: { text: localization.confirm, variant: "danger" },
    });

    if (confirmed) {
      try {
        await repository.approve(item.id);
        this.notificationService.add({ title: localization.approved, type: NotificationType.SUCCESS });
        this.eventBus.publish(approved({ approvable: item }));
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  countryCode(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  chargeType(chargeTypeId: string) {
    return this.enumService.value("charge_types", chargeTypeId)?.name ?? "";
  }

  @bound
  whoAmI(): TApprovalRole | undefined {
    if (this.amIFactualAuditor()) {
      return ApprovalRole.FactualAuditor as unknown as TApprovalRole;
    } else if (this.amICommandOperator()) {
      return ApprovalRole.CommandOperator as unknown as TApprovalRole;
    } else if (this.amIChiefAccountant()) {
      return ApprovalRole.ChiefAccountant as unknown as TApprovalRole;
    }

    return undefined;
  }

  @bound
  approvedByMe(approvable: TApprovable) {
    const currentApprover = this.whoAmI();

    return currentApprover ? (approvable.approvals ?? []).flatMap(item => item.approvalType).includes(currentApprover) : false;
  }

  @bound
  myApprovalStep(approvable: TApprovable) {
    const approvals = (approvable.approvals ?? []).flatMap(item => item.approvalType);

    if (this.amIFactualAuditor()) {
      return this.isCurrentStepReadyOrDoneForFactualAuditor(approvals);
    } else if (this.amICommandOperator()) {
      return this.isCurrentStepReadyOrDoneForCommandOperator(approvals);
    } else if (this.amIChiefAccountant()) {
      return this.isCurrentStepReadyOrDoneForChiefAccountant(approvals);
    }

    return false;
  }

  private amIFactualAuditor() {
    return this.securityService.allowTo("finance.factualauditing");
  }

  private amICommandOperator() {
    return this.securityService.allowTo("finance.commandoperating");
  }

  private amIChiefAccountant() {
    return this.securityService.allowTo("finance.chiefaccounting");
  }

  approvedByApprover(approvable: TApprovable, approver: TApprovalRole) {
    return (approvable.approvals ?? []).flatMap(item => item.approvalType).includes(approver);
  }

  countryOptions() {
    return this.optionsService.enumOptions("countries");
  }

  approvalsOptions() {
    return this.optionsService.internalEnumOptions(ApprovalState);
  }

  allowWrite() {
    return (
      this.securityService.allowTo("finance", "write") &&
      (this.securityService.allowTo("finance.factualauditing", "write") ||
        this.securityService.allowTo("finance.commandoperating", "write") ||
        this.securityService.allowTo("finance.chiefaccounting", "write"))
    );
  }
}
